import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ColumnDef } from '@tanstack/react-table';
import { Copy, MoreHorizontal, Pencil, Plus } from 'lucide-react';
import apiService from '../services/api.service';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { StatusDot } from '../components/ui/status-dot';
import { Toolbar } from '../components/ui/page-header';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { DataTable } from '../components/data-table/DataTable';
import { BulkAction, BulkActionBar } from '../components/data-table/BulkActionBar';
import { FilterChip } from '../components/data-table/FilterChip';
import { SearchField } from '../components/data-table/SearchField';
import { EventPromoCode, PromoCodeFormValues, PromoCodePatch, PromoCodeSheet, promoPayload } from '../components/promo/PromoCodeSheet';
import { useCategories } from '../hooks/useTeams';

// Codes promo d'un événement (refonte 2026-09-27) : tableau, recherche, filtre
// d'état, panneau latéral, activation en lot. Un code se DÉSACTIVE, il ne se
// supprime pas : ses utilisations passées restent rattachées aux paiements.

const promoKey = (eventId: string) => ['event-promo-codes', eventId] as const;

const formatError = (err: any, fallback: string): string => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
  return fallback;
};

export const EventPromoCodesContent: React.FC<{ eventId: string; embedded?: boolean }> = ({ eventId, embedded }) => {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const codesQuery = useQuery({ queryKey: promoKey(eventId), queryFn: async () => ((await apiService.getEventPromoCodes(eventId)) || []) as EventPromoCode[] });
  const categoriesQuery = useCategories(eventId);
  const codes = useMemo(() => codesQuery.data ?? [], [codesQuery.data]);
  const categories = useMemo(() => (categoriesQuery.data ?? []).map((c) => ({ id: c.id, name: c.name })), [categoriesQuery.data]);
  const refresh = () => qc.invalidateQueries({ queryKey: promoKey(eventId) });

  const categoryNames = (ids: string[] | null) => (!ids || ids.length === 0 ? t('promoCodes.allCategories') : ids.map((id) => categories.find((c) => c.id === id)?.name || id).join(', '));
  const formatDiscount = (c: EventPromoCode) => (c.type === 'percentage' ? `${c.value} %` : `${(c.value / 100).toFixed(2)} €`);
  const formatDate = (s: string | null) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—');
  const validity = (c: EventPromoCode) => (c.valid_from || c.valid_until ? `${formatDate(c.valid_from)} → ${formatDate(c.valid_until)}` : t('promoCodes.noLimit'));

  // ----- Filtres -----
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return codes.filter((c) => {
      if (statusFilter.length && !statusFilter.includes(c.is_active ? 'active' : 'inactive')) return false;
      return !q || [c.code, formatDiscount(c), categoryNames(c.applicable_category_ids)].join(' ').toLowerCase().includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codes, query, statusFilter, categories]);

  // ----- Sélection + lot -----
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const selected = codes.filter((c) => selection[c.id]);
  useEffect(() => { setSelection({}); }, [eventId]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const setActive = async (targets: EventPromoCode[], active: boolean) => {
    setBulkBusy(true);
    const failed: string[] = [];
    for (const c of targets) {
      try { await apiService.updateEventPromoCode(eventId, c.id, { is_active: active }); } catch { failed.push(c.code); }
    }
    setBulkBusy(false); setSelection({}); refresh();
    if (failed.length) toast.error(t('promoCodes.list.failed', { codes: failed.join(', ') }));
    else toast.success(t(active ? 'promoCodes.list.activated' : 'promoCodes.list.deactivated', { count: targets.length }));
  };

  // ----- Panneau -----
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [sheetCreate, setSheetCreate] = useState(false);
  const [sheetDirty, setSheetDirty] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const sheetCode = codes.find((c) => c.id === sheetId) ?? null;
  const sheetOpen = sheetCreate || !!sheetCode;
  const idx = filtered.findIndex((c) => c.id === sheetId);
  const openRow = (id: string) => { if (sheetOpen && sheetDirty && id !== sheetId) { setPendingSwitch(id); return; } setSheetCreate(false); setSheetId(id); };
  const closeSheet = () => { setSheetId(null); setSheetCreate(false); setServerError(''); };
  useEffect(() => { closeSheet(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [eventId]);

  // Création seulement ; en édition le panneau écrit champ par champ
  // (`PUT` partiel, clé absente = préservée).
  const submit = async (v: PromoCodeFormValues) => {
    setSaving(true); setServerError('');
    try {
      await apiService.createEventPromoCode(eventId, promoPayload(v));
      toast.success(t('promoCodes.created'));
      setSheetDirty(false); closeSheet(); refresh();
    } catch (err) {
      setServerError(formatError(err, t('promoCodes.saveError')));
    } finally { setSaving(false); }
  };

  const patchCode = async (patch: PromoCodePatch) => {
    if (!sheetCode) return;
    await apiService.updateEventPromoCode(eventId, sheetCode.id, patch);
    refresh();
  };

  const copyCode = async (code: string) => {
    try { await navigator.clipboard.writeText(code); toast.success(t('promoCodes.copied')); } catch { toast.error(t('errors.generic')); }
  };

  const [toDeactivate, setToDeactivate] = useState<EventPromoCode | null>(null);

  const columns = useMemo<ColumnDef<EventPromoCode, any>[]>(() => [
    {
      id: 'code', accessorFn: (c) => c.code, header: () => t('promoCodes.code'),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="font-mono font-semibold text-foreground">{row.original.code}</span>
          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground" aria-label={t('promoCodes.list.copy')} onClick={(e) => { e.stopPropagation(); copyCode(row.original.code); }}><Copy size={12} /></Button>
          {row.original.auto_apply && <span className="text-xs uppercase tracking-[0.06em] text-muted-foreground">{t('promoCodes.list.autoShort')}</span>}
        </div>
      ),
    },
    { id: 'discount', accessorFn: (c) => (c.type === 'percentage' ? c.value : c.value / 100), header: () => t('promoCodes.discount'), cell: ({ row }) => <span className="tabular-nums">{formatDiscount(row.original)}</span> },
    { id: 'categories', accessorFn: (c) => categoryNames(c.applicable_category_ids), header: () => t('promoCodes.categories'), enableSorting: false, cell: ({ getValue }) => <span className="line-clamp-1 max-w-[260px] text-muted-foreground">{getValue()}</span> },
    { id: 'usage', accessorFn: (c) => c.used_count, header: () => t('promoCodes.usage'), cell: ({ row }) => <span className="tabular-nums">{row.original.used_count} / {row.original.max_uses ?? '∞'}</span> },
    { id: 'validity', accessorFn: (c) => c.valid_from ?? '', header: () => t('promoCodes.validity'), cell: ({ row }) => <span className="whitespace-nowrap text-muted-foreground">{validity(row.original)}</span> },
    { id: 'status', accessorFn: (c) => (c.is_active ? 1 : 0), header: () => t('promoCodes.status'), cell: ({ row }) => <StatusDot tone={row.original.is_active ? 'success' : 'neutral'} label={row.original.is_active ? t('promoCodes.active') : t('promoCodes.inactive')} className="text-muted-foreground" /> },
    {
      id: 'actions', enableSorting: false, header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={t('promoCodes.list.rowActions', { code: row.original.code })}><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => openRow(row.original.id)}><Pencil size={16} className="mr-2" />{t('common.edit')}</DropdownMenuItem>
              <DropdownMenuItem onClick={() => copyCode(row.original.code)}><Copy size={16} className="mr-2" />{t('promoCodes.list.copy')}</DropdownMenuItem>
              <DropdownMenuSeparator />
              {row.original.is_active
                ? <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setToDeactivate(row.original)}>{t('promoCodes.deactivate')}</DropdownMenuItem>
                : <DropdownMenuItem onClick={() => setActive([row.original], true)}>{t('promoCodes.list.activate')}</DropdownMenuItem>}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, categories]);

  return (
    <div className={cn('space-y-5 transition-[padding] duration-200', sheetOpen && 'min-[1600px]:pr-[544px]')}>
      <Toolbar>
        <SearchField value={query} onChange={setQuery} placeholder={t('promoCodes.list.searchPlaceholder')} />
        {codes.length > 0 && (
          <FilterChip label={t('promoCodes.status')} selected={statusFilter} onChange={setStatusFilter}
            options={[
              { value: 'active', label: t('promoCodes.active'), count: codes.filter((c) => c.is_active).length },
              { value: 'inactive', label: t('promoCodes.inactive'), count: codes.filter((c) => !c.is_active).length },
            ].filter((o) => o.count > 0)} />
        )}
        {(query || statusFilter.length) ? <span className="text-xs text-muted-foreground">{t('teams.filteredCount', { shown: filtered.length, total: codes.length })}</span> : null}
        <div className="ml-auto flex items-center gap-2">
          {!embedded && <span className="text-sm text-muted-foreground">{t('promoCodes.list.count', { count: codes.length })}</span>}
          <Button size="sm" onClick={() => { setSheetId(null); setSheetCreate(true); }}><Plus size={16} />{t('promoCodes.create')}</Button>
        </div>
      </Toolbar>
      <p className="max-w-3xl text-sm text-muted-foreground">{t('promoCodes.description')}</p>

      {codesQuery.isError && (
        <Alert variant="destructive"><AlertDescription className="flex items-center justify-between gap-4">{t('promoCodes.loadError')}<Button variant="outline" size="sm" onClick={() => codesQuery.refetch()}>{t('common.retry')}</Button></AlertDescription></Alert>
      )}

      <DataTable
        ariaLabel={t('promoCodes.title')}
        columns={columns}
        data={filtered}
        getRowId={(c) => c.id}
        loading={codesQuery.isLoading}
        selectable
        rowSelection={selection}
        onRowSelectionChange={setSelection}
        onRowClick={(c) => openRow(c.id)}
        activeRowId={sheetCode?.id ?? null}
        rowClassName={(c) => (c.is_active ? '' : 'opacity-60')}
        initialSorting={[{ id: 'status', desc: true }]}
        empty={codes.length === 0 ? (
          <div className="space-y-4"><p>{t('promoCodes.empty')}</p><Button onClick={() => setSheetCreate(true)}><Plus size={16} />{t('promoCodes.create')}</Button></div>
        ) : t('promoCodes.list.noMatch')}
        renderCard={(c, { selected: sel, toggle }) => (
          <div className="flex items-start gap-3 py-3">
            <input type="checkbox" checked={sel} onChange={toggle} onClick={(e) => e.stopPropagation()} aria-label={t('dataTable.selectRow')} className="mt-1 h-4 w-4 accent-white" />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3"><span className="font-mono font-semibold">{c.code}</span><span className="tabular-nums">{formatDiscount(c)}</span></div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>{c.used_count} / {c.max_uses ?? '∞'}</span><span>{validity(c)}</span>
                <StatusDot tone={c.is_active ? 'success' : 'neutral'} label={c.is_active ? t('promoCodes.active') : t('promoCodes.inactive')} />
              </div>
            </div>
          </div>
        )}
      />

      <BulkActionBar count={selected.length} onClear={() => setSelection({})}>
        <BulkAction disabled={bulkBusy} onClick={() => setActive(selected, true)}>{t('promoCodes.list.activate')}</BulkAction>
        <BulkAction disabled={bulkBusy} className="text-destructive" onClick={() => setActive(selected, false)}>{t('promoCodes.deactivate')}</BulkAction>
      </BulkActionBar>

      <PromoCodeSheet
        open={sheetOpen}
        promo={sheetCreate ? null : sheetCode}
        categories={categories}
        saving={saving}
        serverError={serverError}
        onSubmit={submit}
        onPatch={patchCode}
        onClose={closeSheet}
        onDirtyChange={setSheetDirty}
        onPrev={idx > 0 ? () => setSheetId(filtered[idx - 1].id) : undefined}
        onNext={idx >= 0 && idx < filtered.length - 1 ? () => setSheetId(filtered[idx + 1].id) : undefined}
      />

      <ConfirmDialog
        open={pendingSwitch !== null}
        onOpenChange={(o) => { if (!o) setPendingSwitch(null); }}
        title={t('teams.form.discardTitle')}
        message={t('teams.form.discardMessage')}
        confirmLabel={t('teams.form.discardConfirm')}
        destructive
        onConfirm={() => { setSheetDirty(false); setSheetCreate(false); setSheetId(pendingSwitch); setPendingSwitch(null); }}
      />
      <ConfirmDialog
        open={toDeactivate !== null}
        onOpenChange={(o) => { if (!o) setToDeactivate(null); }}
        title={t('promoCodes.confirmDelete')}
        message={t('promoCodes.confirmDeleteDesc', { code: toDeactivate?.code ?? '' })}
        confirmLabel={t('promoCodes.deactivate')}
        destructive
        loading={bulkBusy}
        onConfirm={async () => { if (toDeactivate) await setActive([toDeactivate], false); setToDeactivate(null); }}
      />
    </div>
  );
};

export default EventPromoCodesContent;
